import { Mark, mergeAttributes } from '@tiptap/core';

export interface HoleOptions {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  HTMLAttributes: Record<string, string>;
  maxWords?: number;
  hasMaxWords?: boolean;
}

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    hole: {
      /**
       * Set a hole mark
       */
      setHole: () => ReturnType;
      /**
       * Toggle a hole mark
       */
      toggleHole: () => ReturnType;
      /**
       * Unset a hole mark
       */
      unsetHole: () => ReturnType;
    };
  }
}

/**
 * Extension TipTap pour créer des "trous" dans le texte
 * Équivalent du plugin Quill hole pour les jeux "texte à trous"
 */
export const holeExtension = Mark.create<HoleOptions>({
  name: 'hole',

  addOptions() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HTMLAttributes: {
        contenteditable: 'false',
      },
      maxWords: 12,
      hasMaxWords: true,
    };
  },

  addAttributes() {
    return {
      // Pas d'attributs spécifiques pour les trous
    };
  },

  parseHTML() {
    return [
      {
        tag: 'hole',
        getAttrs: (element: HTMLElement): Record<string, string> | false =>
          // Vérifier que c'est bien une balise hole
          element instanceof HTMLElement ? {} : false,
      },
    ];
  },

  // eslint-disable-next-line @typescript-eslint/naming-convention
  renderHTML({ HTMLAttributes }) {
    return [
      'hole',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0, // 0 indique que le contenu va à l'intérieur
    ];
  },

  addCommands() {
    return {
      setHole:
        () =>
        ({ state, chain }): boolean => {
          const { selection } = state;
          const { from, to } = selection;

          // Si pas de sélection, ne rien faire
          if (selection.empty) {
            return false;
          }

          // Récupérer le texte sélectionné
          const selectedText = state.doc.textBetween(from, to);

          // Trouver les positions sans les espaces
          const trimmedText = selectedText.trim();
          if (!trimmedText) {
            return false; // Ne pas créer de trou si que des espaces
          }

          // Calculer les nouvelles positions en excluant les espaces
          const leadingSpaces = selectedText.indexOf(trimmedText);
          const trailingSpaces = selectedText.length - trimmedText.length - leadingSpaces;

          const newFrom = from + leadingSpaces;
          const newTo = to - trailingSpaces;

          // Créer le trou sur le texte trimé
          return chain().setTextSelection({ from: newFrom, to: newTo }).setMark(this.name).run();
        },
      toggleHole:
        () =>
        ({ commands, state, chain }): boolean => {
          const { selection } = state;
          const { from, to } = selection;
          const holeMarkType = state.schema.marks.hole;

          // Cas 1: Si on a une sélection de texte
          if (!selection.empty) {
            // Si le texte sélectionné est déjà un trou, le supprimer
            if (state.doc.rangeHasMark(from, to, holeMarkType)) {
              return commands.unsetMark(this.name);
            }

            // Sinon, créer un trou avec trim automatique
            const selectedText = state.doc.textBetween(from, to);
            const trimmedText = selectedText.trim();

            if (!trimmedText) {
              return false; // Ne pas créer de trou si que des espaces
            }

            // Calculer les nouvelles positions en excluant les espaces
            const leadingSpaces = selectedText.indexOf(trimmedText);
            const trailingSpaces = selectedText.length - trimmedText.length - leadingSpaces;

            const newFrom = from + leadingSpaces;
            const newTo = to - trailingSpaces;

            // Créer le trou sur le texte trimé
            return chain().setTextSelection({ from: newFrom, to: newTo }).setMark(this.name).run();
          }

          // Cas 2: Si le curseur est positionné dans un trou existant (sans sélection)
          // Vérifier si le curseur est dans un nœud avec mark hole
          let isInHole = false;
          state.doc.descendants((node, pos) => {
            if (node.isText && node.marks.length > 0) {
              const holeMark = node.marks.find(mark => mark.type === holeMarkType);
              if (holeMark) {
                const nodeStart = pos;
                const nodeEnd = pos + node.nodeSize;

                // Si le curseur est dans ce nœud avec mark hole
                if (from >= nodeStart && from <= nodeEnd) {
                  isInHole = true;
                  return false; // Arrêter la recherche
                }
              }
            }
            return true;
          });

          // Si le curseur est dans un trou, le supprimer
          if (isInHole) {
            return commands.unsetHole();
          }

          // Sinon, on ne peut pas créer un trou sans sélection
          return false;
        },
      unsetHole:
        () =>
        ({ commands, state, dispatch }): boolean => {
          const { selection } = state;
          const { from } = selection;

          // Si on a une sélection, supprimer le mark sur la sélection
          if (!selection.empty) {
            return commands.unsetMark(this.name);
          }

          // Approche simple : chercher tous les marks hole dans le document
          const holeMarkType = state.schema.marks.hole;
          let foundHole = false;
          let holeStart = -1;
          let holeEnd = -1;

          // Parcourir le document et supprimer tous les marks hole qui contiennent le curseur
          const newTr = state.tr;

          state.doc.descendants((node, pos) => {
            if (node.isText && node.marks.length > 0) {
              // Vérifier si ce nœud texte a un mark hole
              const holeMark = node.marks.find(mark => mark.type === holeMarkType);
              if (holeMark) {
                const nodeStart = pos;
                const nodeEnd = pos + node.nodeSize;

                // Si le curseur est dans ce nœud
                if (from >= nodeStart && from <= nodeEnd) {
                  // Supprimer le mark hole de tout ce nœud
                  newTr.removeMark(nodeStart, nodeEnd, holeMarkType);
                  foundHole = true;
                  holeStart = nodeStart;
                  holeEnd = nodeEnd;
                  return false; // Arrêter la recherche
                }
              }
            }
            return true;
          });

          if (foundHole && dispatch) {
            // Vérifier les espaces avant et après dans l'état original du document
            const prevChar = holeStart > 0 ? state.doc.textBetween(holeStart - 1, holeStart) : '';
            const nextChar = holeEnd < state.doc.content.size ? state.doc.textBetween(holeEnd, holeEnd + 1) : '';

            // Déterminer s'il faut ajouter des espaces
            const needSpaceBefore =
              holeStart > 0 && prevChar && prevChar !== ' ' && prevChar !== '\n' && prevChar !== '\t';
            const needSpaceAfter =
              holeEnd < state.doc.content.size &&
              nextChar &&
              nextChar !== ' ' &&
              nextChar !== '\n' &&
              nextChar !== '\t';

            // Ajouter l'espace avant si nécessaire
            if (needSpaceBefore) {
              newTr.insertText(' ', holeStart);
              holeEnd += 1; // Ajuster la position de fin car on a inséré un caractère
            }

            // Ajouter l'espace après si nécessaire
            if (needSpaceAfter) {
              newTr.insertText(' ', holeEnd);
            }

            dispatch(newTr);
            return true;
          }

          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Raccourci clavier pour créer/supprimer un trou
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Mod-h': (): boolean => this.editor.commands.toggleHole(),
    };
  },
});
